import type { CSSProperties, ReactNode } from "react";
import { Icon, type IconName } from "../icons/Icon";
import "../../styles/components/feedback.css";

export interface EmptyStateProps {
  /** 缺省用 inbox；列表类页面建议传对应业务图标 */
  icon?: IconName;
  title: string;
  description?: ReactNode;
  /** 引导动作（必须有，空态不能是死路） */
  action?: ReactNode;
  compact?: boolean;
}

/** 空状态：统一「图标 + 标题 + 说明 + 引导按钮」，避免各页面各写一套 */
export function EmptyState({ icon = "inbox", title, description, action, compact = false }: EmptyStateProps) {
  return (
    <div className={`empty-state${compact ? " is-compact" : ""}`}>
      <div className="empty-icon">
        <Icon name={icon} size={compact ? 22 : 28} />
      </div>
      <div className="empty-title">{title}</div>
      {description ? <div className="empty-desc">{description}</div> : null}
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

export interface SkeletonProps {
  width?: number | string;
  height?: number;
  radius?: number;
  className?: string;
  style?: CSSProperties;
}

/** 骨架块：加载态统一用它，替代转圈，避免布局跳动 */
export function Skeleton({ width = "100%", height = 14, radius = 6, className = "", style }: SkeletonProps) {
  return (
    <span
      className={`skeleton${className ? " " + className : ""}`}
      style={{ width, height, borderRadius: radius, ...style }}
      aria-hidden="true"
    />
  );
}

/** 多行文本骨架 */
export function SkeletonText({ lines = 3, gap = 8 }: { lines?: number; gap?: number }) {
  return (
    <div className="skeleton-text" style={{ gap }} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? "62%" : "100%"} />
      ))}
    </div>
  );
}

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: "brand" | "green" | "amber" | "red";
  height?: number;
  /** 右侧显示百分比 */
  showLabel?: boolean;
  label?: ReactNode;
}

/** 进度条：用于工单进度、齐套率、在制品完成度等 */
export function ProgressBar({ value, max = 100, tone = "brand", height = 6, showLabel = false, label }: ProgressBarProps) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="progress-wrap">
      {label ? <div className="progress-label">{label}</div> : null}
      <div className="progress-row">
        <div className="progress-track" style={{ height }}>
          <div className={`progress-fill progress-${tone}`} style={{ width: `${pct}%` }} />
        </div>
        {showLabel ? <span className="progress-value">{pct.toFixed(0)}%</span> : null}
      </div>
    </div>
  );
}

export default EmptyState;
